import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentOrganization } from '@/hooks/use-current-organization'
import { unwrapApi } from '@/hooks/use-api'
import { api } from '@/lib/axios'
import {
  isWebsiteIndexingInFlight,
  type OrganizationWebsite,
  type CreateOrganizationWebsiteDto,
  type UpdateOrganizationWebsiteDto,
} from '@cmoh/shared'

const QUERY_KEY_BASE = 'organization-websites'
const POLL_INTERVAL_MS = 4000

export function useOrganizationWebsites() {
  const { organization } = useCurrentOrganization()
  const organizationId = organization?.id

  return useQuery({
    queryKey: [QUERY_KEY_BASE, organizationId],
    queryFn: async () => {
      const response = await api.get<OrganizationWebsite[]>('/api/organization/websites')
      return unwrapApi(response)
    },
    enabled: Boolean(organizationId),
    // Polling follows the same staleness rule as the UI: a run whose poll chain died leaves the
    // row on an in-progress status forever, and keying on the raw status would keep every open
    // tab hitting the API for a crawl that will never report back.
    refetchInterval: (query) => {
      const websites = query.state.data
      if (!websites) return false
      return websites.some((website) =>
        isWebsiteIndexingInFlight(website.indexingStatus, website.updatedAt),
      )
        ? POLL_INTERVAL_MS
        : false
    },
  })
}

export function useCreateOrganizationWebsite() {
  const queryClient = useQueryClient()
  const { organization } = useCurrentOrganization()
  const organizationId = organization?.id

  return useMutation({
    mutationFn: async (data: CreateOrganizationWebsiteDto) => {
      const response = await api.post<OrganizationWebsite>('/api/organization/websites', data)
      return unwrapApi(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, organizationId] })
    },
  })
}

export function useUpdateOrganizationWebsite() {
  const queryClient = useQueryClient()
  const { organization } = useCurrentOrganization()
  const organizationId = organization?.id

  return useMutation({
    mutationFn: async ({ websiteId, data }: { websiteId: string; data: UpdateOrganizationWebsiteDto }) => {
      const response = await api.patch<OrganizationWebsite>(
        `/api/organization/websites/${websiteId}`,
        data,
      )
      return unwrapApi(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, organizationId] })
    },
  })
}

export function useReindexOrganizationWebsite() {
  const queryClient = useQueryClient()
  const { organization } = useCurrentOrganization()
  const organizationId = organization?.id

  return useMutation({
    mutationFn: async (websiteId: string) => {
      const response = await api.post<OrganizationWebsite>(
        `/api/organization/websites/${websiteId}/reindex`,
      )
      return unwrapApi(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, organizationId] })
    },
  })
}

export function useDeleteOrganizationWebsite() {
  const queryClient = useQueryClient()
  const { organization } = useCurrentOrganization()
  const organizationId = organization?.id

  return useMutation({
    mutationFn: async (websiteId: string) => {
      await api.delete(`/api/organization/websites/${websiteId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE, organizationId] })
    },
  })
}
